// components/steps/PaymentStep.tsx - Complete updated version
import React, { useState, useEffect } from 'react';
import { api } from '../../config/api.ts';
import { Client, Course, Plan } from '../../types/index.ts';
import { ModernCard } from '../ui/ModernCard.tsx';
import { ModernButton } from '../ui/ModernButton.tsx';
import { FileUpload } from '../FileUpload.tsx';
import { ChevronLeftIcon } from '../icons/index.tsx';
import { CouponInput } from '../ui/CouponInput.tsx';
import { useCoupon } from '../../hooks/useCoupon.ts';

interface UploadedFile {
  id: number;
  name: string;
  size?: number;
  type?: string;
  uploaded_at: string;
}

interface PaymentStepProps {
  client: Client;
  selectedCourse: Course;
  selectedPlan: Plan;
  selectedDeliverables: string[];
  paymentBreakdown: any;
  calculatedPrice: number;
  planDetails: any;
  onSuccess: (taskData?: any) => void;
  onBack: () => void;
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
  sessionId: string;
  appliedCoupon?: any;
  onCouponChange?: (coupon: any) => void;
}

export const PaymentStep: React.FC<PaymentStepProps> = ({
  client,
  selectedCourse,
  selectedPlan,
  selectedDeliverables,
  paymentBreakdown,
  calculatedPrice,
  planDetails,
  onSuccess,
  onBack,
  setError,
  setLoading,
  sessionId,
  appliedCoupon,
  onCouponChange
}) => {
  const [selectedPaymentOption, setSelectedPaymentOption] = useState<any>(
    paymentBreakdown?.payment_options?.find((opt: any) => opt.recommended) || 
    paymentBreakdown?.payment_options?.[0]
  );
  
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [recalculatedBreakdown, setRecalculatedBreakdown] = useState<any>(paymentBreakdown);
  const [finalPrice, setFinalPrice] = useState(calculatedPrice);
  const [labFiles, setLabFiles] = useState<UploadedFile[]>([]);
const [labSessionId] = useState(() => `lab_${sessionId}`); 

  const { validateCoupon, removeCoupon, loading: couponLoading } = useCoupon();
  
  const handleFilesChange = (files: UploadedFile[]) => {
    setUploadedFiles(files);
  };

  // Recalculate price and payment options when coupon changes
  useEffect(() => {
    if (appliedCoupon) {
      recalculateWithCoupon();
    } else {
      // Reset to original values
      setFinalPrice(calculatedPrice);
      setRecalculatedBreakdown(paymentBreakdown);
    }
  }, [appliedCoupon, calculatedPrice, paymentBreakdown]);

  const recalculateWithCoupon = async () => {
    if (!appliedCoupon || !selectedPlan) return;

    try {
      const response = await api.post('/client/tasks/calculate-cost', {
        plan_id: selectedPlan.id,
        deliverables: selectedDeliverables,
        coupon_code: appliedCoupon.code
      });

      if (response.data.success) {
        setFinalPrice(response.data.data.total_price);
        setRecalculatedBreakdown(response.data.data);
        
        // Update selected payment option to the recommended one
        const recommendedOption = response.data.data.payment_options?.find((opt: any) => opt.recommended);
        if (recommendedOption) {
          setSelectedPaymentOption(recommendedOption);
        }
      }
    } catch (error: any) {
      setError('فشل في إعادة حساب السعر مع الكوبون');
    }
  };

  const handleApplyCoupon = async (code: string) => {
    try {
      const couponData = await validateCoupon(code, calculatedPrice);
      if (onCouponChange) {
        onCouponChange(couponData);
      }
      setError(''); // Clear any previous errors
    } catch (error: any) {
      setError(error.message);
    }
  };
const handleLabFilesChange = (files: UploadedFile[]) => {
  setLabFiles(files);
};
  const handleRemoveCoupon = () => {
    removeCoupon();
    if (onCouponChange) {
      onCouponChange(null);
    }
    setError('');
  };

  const createTask = async () => {
    try {
      setIsLoading(true);
      setLoading(true);
      
      if (uploadedFiles.length === 0) {
        setError('يجب رفع ملف واحد على الأقل قبل إنشاء المهمة');
        setIsLoading(false);
        setLoading(false);
        return;
      }
      if (selectedPlan.required_fields.lab_file && labFiles.length === 0) {
  setError('يجب رفع ملف المختبر المطلوب');
  setIsLoading(false);
  setLoading(false);
  return;
}
      if (selectedPaymentOption && selectedPaymentOption.payment_needed > 0) {
        await initiatePayment();
        return;
      }
      
  const taskData: any = {
  plan_id: selectedPlan?.id,
  deliverables: selectedDeliverables,
  session_id: sessionId,
  lab_session_id: selectedPlan.required_fields.lab_file ? labSessionId : null,
  ...planDetails
};

      // Add coupon if applied
      if (appliedCoupon) {
        taskData.coupon_code = appliedCoupon.code;
      }

      if (selectedPaymentOption) {
        taskData.use_balance = selectedPaymentOption.use_balance;
        taskData.use_credit = selectedPaymentOption.use_credit;
      }

      const response = await api.post('/client/tasks/create-direct', taskData);
      
      if (response.data.success) {
        const createdTaskData = {
          id: response.data.data.task.id,
          public_id: response.data.data.task.public_id,
          title: response.data.data.task.title || selectedPlan?.title,
          due_date: response.data.data.task.due_date,
          status: response.data.data.task.status,
          payment_breakdown: response.data.data.payment_breakdown
        };
        
        onSuccess(createdTaskData);
      }
    } catch (err: any) {
      if (err.response?.status === 402) {
        await initiatePayment();
      } else {
        setError('فشل في إنشاء المهمة: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const initiatePayment = async () => {
    try {
      setIsLoading(true);
      setLoading(true);
      
      if (uploadedFiles.length === 0) {
        setError('يجب رفع ملف واحد على الأقل قبل المتابعة للدفع');
        setIsLoading(false);
        setLoading(false);
        return;
      }
if (selectedPlan.required_fields.lab_file && labFiles.length === 0) {
  setError('يجب رفع ملف المختبر المطلوب');
  setIsLoading(false);
  setLoading(false);
  return;
}
     const paymentData: any = {
  plan_id: selectedPlan?.id,
  deliverables: selectedDeliverables,
  task_data: {
    ...planDetails,
    session_id: sessionId,
    lab_session_id: selectedPlan.required_fields.lab_file ? labSessionId : null
  }
};

      // Add coupon if applied
      if (appliedCoupon) {
        paymentData.coupon_code = appliedCoupon.code;
      }

      if (selectedPaymentOption) {
        paymentData.use_balance = selectedPaymentOption.use_balance;
        paymentData.use_credit = selectedPaymentOption.use_credit;
      }

      const response = await api.post('/client/tasks/initiate-payment', paymentData);
      
      if (response.data.success) {
        window.location.href = response.data.data.invoice_url;
      }
    } catch (err) {
      setError('فشل في بدء عملية الدفع');
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  // Use recalculated values if coupon is applied, otherwise use original
  const currentBreakdown = appliedCoupon ? recalculatedBreakdown : paymentBreakdown;
  const currentPrice = appliedCoupon ? finalPrice : calculatedPrice;

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">المراجعة والدفع</h2>
        <p className="text-gray-600">تأكد من تفاصيل مهمتك ورفع الملفات</p>
      </div>

      {/* File Upload Section */}
     {/* File Upload Section */}
<div className="space-y-6">
  {/* Regular Task Files */}
  <div>
    <h3 className="text-lg font-semibold text-gray-900 mb-4">
      رفع الملفات
      <span className="text-red-500 mr-1">*</span>
    </h3>
    <p className="text-sm text-gray-600 mb-3">
      قم برفع ملفات المهمة المطلوبة
    </p>
    <FileUpload
      sessionId={sessionId}
      onFilesChange={handleFilesChange}
      maxFiles={10}
      maxSize={20}
      acceptedTypes=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.zip"
      endpoint="/client/tasks/upload-files"
      deleteEndpoint="/client/tasks/delete-files"
      mode="task"
    />
    {uploadedFiles.length > 0 ? (
      <p className="text-sm text-green-600 mt-2">
        ✓ تم رفع {uploadedFiles.length} ملف بنجاح
      </p>
    ) : (
      <p className="text-sm text-orange-600 mt-2">
        ⚠️ يجب رفع ملف واحد على الأقل
      </p>
    )}
  </div>

  {/* Lab Files Section - Only show if required */}
  {selectedPlan.required_fields.lab_file && (
    <ModernCard className="p-6 border-2 border-yellow-200 bg-yellow-50">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        ملفات المختبر <span className="text-red-500">*</span>
      </h3>
      <p className="text-sm text-gray-700 mb-4">
        يجب رفع ملف المختبر المطلوب للمهمة
      </p>
      <FileUpload
        sessionId={labSessionId}
        onFilesChange={handleLabFilesChange}
        maxFiles={5}
        maxSize={20}
        acceptedTypes=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.zip"
        endpoint="/client/tasks/upload-files"
        deleteEndpoint="/client/tasks/delete-files"
        mode="lab"
      />
      {labFiles.length > 0 ? (
        <p className="text-sm text-green-600 mt-2">
          ✓ تم رفع {labFiles.length} ملف مختبر بنجاح
        </p>
      ) : (
        <p className="text-sm text-red-600 mt-2">
          ⚠️ ملف المختبر مطلوب
        </p>
      )}
    </ModernCard>
  )}
</div>

      {/* Coupon Section */}
 

      {/* Client Info */}
      <ModernCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">معلومات الحساب</h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">الاسم:</span>
            <span className="font-medium">{client.full_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">الهاتف:</span>
            <span className="font-medium">{client.phone}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">الرصيد:</span>
            <span className="font-medium text-green-600">{client.balance} KWD</span>
          </div>
          {client.is_trusted && (
            <div className="flex justify-between">
              <span className="text-gray-600">الائتمان المتاح:</span>
              <span className="font-medium text-blue-600">
                {client.credit_limit - client.credit_used} KWD
              </span>
            </div>
          )}
        </div>
      </ModernCard>

      {/* Task Summary */}
      <ModernCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">ملخص المهمة</h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">المادة:</span>
            <span className="font-medium">{selectedCourse?.display_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">الخطة:</span>
            <span className="font-medium">{selectedPlan?.title}</span>
          </div>
          {selectedDeliverables.length > 0 && (
            <div>
              <span className="text-gray-600">المخرجات:</span>
              <div className="mt-1 flex flex-wrap gap-1">
                {selectedDeliverables.map(deliverable => (
                  <span key={deliverable} className="inline-flex px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {deliverable}
                  </span>
                ))}
              </div>
            </div>
          )}
          {planDetails.due_date && (
  <div className="flex justify-between">
    <span className="text-gray-600">موعد التسليم:</span>
    <span className="font-medium">
      {new Date(planDetails.due_date).toLocaleString('ar-JO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}
    </span>
  </div>
)}
          {uploadedFiles.length > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-600">الملفات المرفقة:</span>
              <span className="font-medium text-green-600">{uploadedFiles.length} ملف</span>
            </div>
          )}
          {selectedPlan.required_fields.lab_file && labFiles.length > 0 && (
  <div className="flex justify-between">
    <span className="text-gray-600">ملفات المختبر:</span>
    <span className="font-medium text-green-600">{labFiles.length} ملف</span>
  </div>
)}
        </div>
      </ModernCard>
               <ModernCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">أدخل كود الخصم إن وجد</h3>
        <CouponInput
          onApplyCoupon={handleApplyCoupon}
          onRemoveCoupon={handleRemoveCoupon}
          appliedCoupon={appliedCoupon}
          loading={couponLoading}
          disabled={isLoading}
        />
      </ModernCard>
      {/* Payment Breakdown */}
      <ModernCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">تفاصيل الدفع</h3>
        <div className="space-y-3">
          {appliedCoupon && (
            <>
              <div className="flex justify-between text-gray-600">
                <span>السعر الأصلي:</span>
                <span className="line-through">{currentBreakdown?.original_price || calculatedPrice} KWD</span>
              </div>
              <div className="flex justify-between text-green-600">
                <span>الخصم ({appliedCoupon.code}):</span>
                <span>-{currentBreakdown?.discount_amount || appliedCoupon.discount_amount} KWD</span>
              </div>
              <hr className="my-2" />
            </>
          )}
          
          <div className="flex justify-between">
            <span className="text-gray-600">تكلفة المهمة:</span>
            <span className="font-medium">{currentPrice} KWD</span>
          </div>
          
          {client.is_trusted && (
            <div className="flex justify-between">
              <span className="text-gray-600">الائتمان المتاح:</span>
              <span className="font-medium text-blue-600">
                {currentBreakdown?.client_balance?.available_credit || (client.credit_limit - client.credit_used)} KWD
              </span>
            </div>
          )}
          
          <hr className="my-3" />
          
          <div className="flex justify-between font-semibold">
            <span>حالة الدفع:</span>
            <span className={currentBreakdown?.can_afford ? 'text-green-600' : 'text-orange-600'}>
              {currentBreakdown?.can_afford ? 'الرصيد كافي' : 'مطلوب دفع خارجي'}
            </span>
          </div>
          
          {!currentBreakdown?.can_afford && (
            <div className="flex justify-between text-orange-600">
              <span>الدفع المطلوب:</span>
              <span className="font-medium">{currentBreakdown?.payment_required} KWD</span>
            </div>
          )}
        </div>

        {/* Payment Options for Trusted Users */}
        {client.is_trusted && currentBreakdown?.payment_options && currentBreakdown.payment_options.length > 0 && (
          <div className="mt-6 p-4 bg-blue-50 rounded-xl">
            <h4 className="font-semibold text-blue-900 mb-3">خيارات الدفع</h4>
            <div className="space-y-3">
              {currentBreakdown.payment_options.map((option: any) => (
                <div 
                  key={option.name} 
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedPaymentOption?.name === option.name
                      ? 'border-blue-500 bg-blue-100 ring-2 ring-blue-200'
                      : option.recommended 
                        ? 'border-blue-300 bg-blue-50' 
                        : 'border-gray-200 bg-white hover:border-blue-200'
                  }`}
                  onClick={() => setSelectedPaymentOption(option)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {option.use_credit > 0 && option.payment_needed > 0 
                          ? 'دفع مختلط (إعانة + دفع)' 
                          : option.use_credit > 0 
                            ? 'دفع على الإعانة' 
                            : 'دفع الحين'
                        }
                        {option.recommended && (
                          <span className="mr-2 text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                            موصى به
                          </span>
                        )}
                        {selectedPaymentOption?.name === option.name && (
                          <span className="mr-2 text-xs bg-green-500 text-white px-2 py-1 rounded-full">
                            محدد
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        الائتمان: {option.use_credit} KWD
                        {option.payment_needed > 0 && ` | دفع خارجي: ${option.payment_needed} KWD`}
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        selectedPaymentOption?.name === option.name
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedPaymentOption?.name === option.name && (
                          <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </ModernCard>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <ModernButton
          variant="outline"
          onClick={onBack}
          className="flex-1"
          icon={<ChevronLeftIcon />}
        >
          رجوع
        </ModernButton>
        
        <ModernButton
          variant="success"
          onClick={createTask}
          className="flex-1"
          loading={isLoading}
disabled={
  !selectedPaymentOption || 
  uploadedFiles.length === 0 || 
  (selectedPlan.required_fields.lab_file && labFiles.length === 0)
}        >
          {selectedPaymentOption?.use_credit > 0 && selectedPaymentOption?.payment_needed > 0
            ? 'دفع مختلط وإنشاء المهمة'
            : selectedPaymentOption?.use_credit > 0
              ? 'دفع على الإعانة وإنشاء المهمة'
              : 'دفع الحين وإنشاء المهمة'
          }
        </ModernButton>
      </div>
    </div>
  );
};